<template>
  <div
    v-if="isOnRequest"
    class="lot-side-surface flex flex-col gap-1 rounded-2xl px-4 py-5"
  >
    <div class="text-base font-bold leading-6 text-gray-900">
      {{ $t('lots.show.prices.onRequest') }}
    </div>
    <p class="text-sm font-medium leading-5 text-gray-600">
      {{ $t('lots.show.prices.onRequestHint') }}
    </p>
  </div>
  <div
    v-else-if="hasAnyPrices"
    class="lot-side-surface flex flex-col gap-4 rounded-2xl px-4 py-5"
  >
    <div class="flex w-full">
      <div
        v-for="(item, index) in suggestedRentPeriodPrices"
        :key="index"
        class="flex min-w-0 flex-1 items-center"
        :class="periodColumnAlign(index)"
      >
        <div class="flex flex-col gap-[2px]">
          <div class="whitespace-nowrap text-sm font-semibold leading-5 text-gray-600">
            {{ item.period }}
            {{ $t(item.kind === 'DAY' ? 'common.day' : 'common.hour', item.period) }}
          </div>
          <div class="whitespace-nowrap text-base font-bold leading-6 text-green-500">
            {{ item.price.toLocaleString($i18n.locale) }}&nbsp;{{ currencyChar }}
          </div>
        </div>
      </div>
    </div>

    <template v-if="isOpened">
      <div class="h-px w-full shrink-0 bg-gray-300" />

      <div class="flex w-full flex-col gap-4 text-sm">
        <div
          v-if="hasHourlyRates"
          class="flex flex-col gap-2"
        >
          <div
            v-if="hasDailyRates"
            class="font-semibold text-gray-900"
          >
            Почасовые
          </div>
          <div
            v-for="(item, index) in prices.hourlyRates"
            :key="`hour-${index}`"
            class="flex items-center justify-between"
          >
            <span class="font-semibold text-gray-600">
              от {{ item.minHours }} {{ $t('common.hours', item.minHours) }}
            </span>
            <span class="text-right font-bold text-gray-900">
              {{ item.price.toLocaleString($i18n.locale) }}&nbsp;{{ currencyChar }}/час
            </span>
          </div>
        </div>

        <div
          v-if="hasDailyRates"
          class="flex flex-col gap-2"
        >
          <div
            v-if="hasHourlyRates"
            class="font-semibold text-gray-900"
          >
            Посуточные
          </div>
          <div
            v-for="(item, index) in prices.dailyRates"
            :key="`day-${index}`"
            class="flex items-center justify-between"
          >
            <span class="font-semibold text-gray-600">
              от {{ item.minDays }} {{ $t('common.days', item.minDays) }}
            </span>
            <span class="text-right font-bold text-gray-900">
              {{ item.price.toLocaleString($i18n.locale) }}&nbsp;{{ currencyChar }}/день
            </span>
          </div>
        </div>
      </div>
    </template>

    <button
      type="button"
      class="flex items-center gap-1 py-0.5 text-sm font-medium text-blue-500"
      @click="handleTogglePrices"
    >
      {{ isOpened ? 'Скрыть' : 'Все тарифы' }}
      <ChevronIcon
        :class="{ 'rotate-180': !isOpened }"
        class="size-5 shrink-0 text-blue-500"
      />
    </button>
  </div>
</template>

<script setup>
import { buildAnalyticsStore } from '~/analytics/composables-types'
import ChevronIcon from '~/assets/svg/chevron-down.svg?component'
import { useLot } from '~/composables/useLot'
import { getViewRatesAmplitudePayload } from '~/composables/useViewRatesAnalytics'

const props = defineProps({
  lotId: {
    type: [String, Number],
    required: true,
  },
  prices: {
    type: Object,
    default: () => ({}),
  },
  suggestedRentPeriodPrices: {
    type: Array,
    default: () => [],
  },
})

const { $tracking } = useNuxtApp()
const { lot, currency } = useLot(props.lotId)

const isOpened = ref(false)

const currencyChar = computed(() => (currency.value !== 'RUB' ? '$' : '₽'))

const hasHourlyRates = computed(
  () => props.prices?.hourlyRates?.length > 0 && props.prices?.hourlyRentAvailable,
)

const hasDailyRates = computed(() => props.prices?.dailyRates?.length > 0)

const isOptionalPrice = computed(() => Boolean(lot.value?.category?.optionalPrice))

const hasAnyPrices = computed(() => {
  return (
    props.suggestedRentPeriodPrices?.length > 0 ||
    hasHourlyRates.value ||
    hasDailyRates.value
  )
})

const isOnRequest = computed(() => isOptionalPrice.value && !hasAnyPrices.value)

const periodColumnAlign = (index) => {
  const lastIndex = props.suggestedRentPeriodPrices.length - 1

  if (index === 0) {
    return 'justify-start'
  }
  if (index === lastIndex) {
    return 'justify-end'
  }
  return 'justify-center'
}

const handleTogglePrices = () => {
  isOpened.value = !isOpened.value

  if (isOpened.value) {
    $tracking.logEventAmplitude(
      'view rates',
      getViewRatesAmplitudePayload({
        store: buildAnalyticsStore(),
        lot: lot.value,
      }),
    )
  }
}
</script>
